import API from 'constants/api';
import rest from 'util/Api';

const uuid = () =>
  window.crypto?.randomUUID?.() ??
  `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const urlHasContent = (url) =>
  new Promise((resolve) => {
    const img = new window.Image();
    img.onload = () => resolve(img.naturalWidth > 0 && img.naturalHeight > 0);
    img.onerror = () => resolve(false);
    img.src = url;
  });

export const getDeliveryHistory = async (shipmentId) => {
  try {
    const {
      data: { history },
    } = await rest.get(API.delivery.historyByShipmentId(shipmentId));
    return history ?? [];
  } catch {
    return [];
  }
};

export const getVisitHistory = async (shipmentId) => {
  try {
    const {
      data: { visits },
    } = await rest.get(API.delivery.visitsByShipmentId(shipmentId));
    return visits ?? [];
  } catch {
    return [];
  }
};

export const podEntries = async (history, signatureImage) => {
  const entries = (history[history.length - 1]?.entries ?? []).filter(
    (entry) => entry.url,
  );
  if (
    signatureImage &&
    !entries.some((entry) => entry.type === 'signature') &&
    (await urlHasContent(signatureImage))
  ) {
    entries.push({ type: 'signature', url: signatureImage });
  }
  return entries;
};

export const keptSignatureUrl = (history, keptUrls) => {
  const entries = history[history.length - 1]?.entries ?? [];
  return keptUrls.find((url) =>
    entries.some((entry) => entry.url === url && entry.type === 'signature'),
  );
};

export const podRecipient = (history) =>
  (history[history.length - 1]?.entries ?? []).find(
    (entry) => entry.signatureData,
  )?.signatureData ?? {};

export const getDeliveryEntries = async (shipmentId, signatureImage) =>
  podEntries(await getDeliveryHistory(shipmentId), signatureImage);

export const waitForNewDelivery = async (shipmentId, previousLength) => {
  let history = [];
  for (let attempt = 0; attempt < 8; attempt += 1) {
    // eslint-disable-next-line no-await-in-loop
    history = await getDeliveryHistory(shipmentId);
    if (history.length > previousLength) return { history, appeared: true };
    // eslint-disable-next-line no-await-in-loop
    await new Promise((resolve) => {
      setTimeout(resolve, 1000);
    });
  }
  return { history, appeared: false };
};

const SIGNATURE_PROCESS_TYPE = 'SIGNATURE_DELIVERY';

export const createWebDelivery = async (
  shipmentId,
  { nombre, dni, tramitNumber, location, signatureUrl, imageUrls = [] },
) => {
  const submissionId = Date.now();
  const substepData = { nombre, dni, tramitNumber };
  if (location) substepData.location = location;
  const substep = (key, extra) => ({
    id: uuid(),
    submissionId,
    key,
    status: 'COMPLETED',
    ...extra,
  });
  return rest.post(API.delivery.create, {
    id: uuid(),
    submissionId,
    shipmentId: String(shipmentId),
    processType: SIGNATURE_PROCESS_TYPE,
    createdAt: new Date().toISOString(),
    status: 'COMPLETED',
    origin: 'WEB',
    substeps: [
      substep(
        'signature',
        signatureUrl
          ? {
              type: 'image',
              data: substepData,
              image: { firebaseUrl: signatureUrl },
            }
          : { type: 'data', data: substepData },
      ),
      ...imageUrls.map((firebaseUrl, index) =>
        substep(`web_photo_${index + 1}`, {
          type: 'image',
          image: { firebaseUrl },
        }),
      ),
    ],
  });
};

const VISIT_PROCESS_TYPE = 'VISIT';

export const createWebVisit = async (
  shipmentId,
  { editsSubmissionId, reason, notes, location, imageUrls = [] },
) => {
  const submissionId = Date.now();
  const substep = (key, extra) => ({
    id: uuid(),
    submissionId,
    key,
    status: 'COMPLETED',
    ...extra,
  });
  return rest.post(API.delivery.attempt, {
    id: uuid(),
    submissionId,
    shipmentId: String(shipmentId),
    processType: VISIT_PROCESS_TYPE,
    createdAt: new Date().toISOString(),
    status: 'COMPLETED',
    origin: 'WEB',
    substeps: [
      substep('attempt', {
        type: 'attempt',
        data: {
          editsSubmissionId: String(editsSubmissionId),
          reason,
          notes,
          ...(location ? { location } : {}),
        },
      }),
      ...imageUrls.map((firebaseUrl, index) =>
        substep(`web_photo_${index + 1}`, {
          type: 'image',
          image: { firebaseUrl },
        }),
      ),
    ],
  });
};

export const waitForVisitEdit = async (
  shipmentId,
  numberVisit,
  previousSubmissionId,
) => {
  let visits = [];
  for (let attempt = 0; attempt < 8; attempt += 1) {
    // eslint-disable-next-line no-await-in-loop
    visits = await getVisitHistory(shipmentId);
    const edited = visits.find((visit) => visit.numberVisit === numberVisit);
    if (edited && String(edited.submissionId) !== String(previousSubmissionId))
      return { visits, appeared: true };
    // eslint-disable-next-line no-await-in-loop
    await new Promise((resolve) => {
      setTimeout(resolve, 1000);
    });
  }
  return { visits, appeared: false };
};

export default getDeliveryEntries;
